import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CheckCircle, AlertCircle, CreditCard } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Card embedded in the cleaner dashboard that lets them connect a Stripe
 * Express account so payouts are ready when bookings open.
 */
export default function StripeConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/cleaners/stripe/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not check status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // If user is returning from Stripe onboarding, refresh status
    const sp = new URLSearchParams(window.location.search);
    if (sp.has('stripe_complete') || sp.has('stripe_refresh')) {
      const t = setTimeout(fetchStatus, 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const startOnboarding = async () => {
    setBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/cleaners/stripe/connect-link`,
        { return_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start Stripe onboarding');
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-2xl p-5 border border-border flex items-center justify-center text-text-secondary text-sm" data-testid="stripe-connect-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking payout setup...
      </div>
    );
  }

  const isReady = status?.payouts_enabled;
  const inProgress = status?.connected && !isReady;

  return (
    <div className="bg-surface rounded-2xl p-6 border border-border" data-testid="stripe-connect-card">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${
          isReady ? 'bg-green-100 text-green-700' : inProgress ? 'bg-amber-100 text-amber-700' : 'bg-primary-light text-primary'
        }`}>
          {isReady ? <CheckCircle className="w-6 h-6" /> : <CreditCard className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-semibold text-text-primary mb-1">
            {isReady ? 'Payouts ready' : 'Set up payouts'}
          </h3>
          {isReady ? (
            <p className="text-sm text-text-secondary">
              Your bank is connected via Stripe. When bookings open, your earnings will be deposited automatically.
            </p>
          ) : inProgress ? (
            <p className="text-sm text-text-secondary">
              Almost done — Stripe still needs a few details from you.
              {status?.requirements_due?.length > 0 && (
                <span className="block mt-1 text-xs">
                  Missing: {status.requirements_due.slice(0, 3).join(', ')}{status.requirements_due.length > 3 ? '…' : ''}
                </span>
              )}
            </p>
          ) : (
            <p className="text-sm text-text-secondary">
              Connect a bank account so we&apos;re ready to pay you the moment bookings open. Takes about 2 minutes — Stripe handles it securely.
            </p>
          )}

          {!isReady && (
            <button
              type="button"
              onClick={startOnboarding}
              disabled={busy}
              className="mt-4 btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-60"
              data-testid="connect-stripe-btn"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {inProgress ? 'Finish on Stripe →' : 'Connect your bank →'}
            </button>
          )}

          {error && (
            <p className="mt-3 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
